import { useRef, useState } from 'react';

const PULL_THRESHOLD = 70;

/**
 * emoji-sheet-swipe — put on an overlay's root (BottomSheet does) so a touch
 * that starts inside it is never a pull on the screen below. Sheets are
 * rendered inside the screen and React touch events bubble up to it: without
 * this, a dismiss swipe in a sheet refreshed the feed.
 */
export const PULL_REFRESH_IGNORE_ATTR = 'data-pull-refresh-ignore';

/** Spread onto an overlay's root: `<div {...pullRefreshIgnore}>`. */
export const pullRefreshIgnore = { [PULL_REFRESH_IGNORE_ATTR]: '' };

function startsInsideOverlay(e: React.TouchEvent<HTMLElement>) {
  const overlay =
    e.target instanceof Element ? e.target.closest(`[${PULL_REFRESH_IGNORE_ATTR}]`) : null;
  return !!overlay && e.currentTarget.contains(overlay);
}

/**
 * Pull-to-refresh for a vertically scrollable container.
 *
 * Spread `handlers` onto the scroll element (which must be `position: relative`
 * so the spinner can anchor to its top) and render <PullToRefreshSpinner> as its
 * first child. Pulling down past the threshold from the very top triggers
 * `onRefresh`. Extracted from the Home deck so every screen behaves identically.
 */
export function usePullToRefresh(onRefresh: () => Promise<unknown>) {
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullStart = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent<HTMLElement>) => {
    if (startsInsideOverlay(e)) return;
    if (e.currentTarget.scrollTop <= 0) pullStart.current = e.touches[0].clientY;
  };
  const onTouchMove = (e: React.TouchEvent<HTMLElement>) => {
    if (pullStart.current == null) return;
    const delta = e.touches[0].clientY - pullStart.current;
    if (delta > 0 && e.currentTarget.scrollTop <= 0) setPullY(Math.min(delta * 0.5, 90));
    else pullStart.current = null;
  };
  const onTouchEnd = async () => {
    if (pullY >= PULL_THRESHOLD && !refreshing) {
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
      }
    }
    setPullY(0);
    pullStart.current = null;
  };

  return {
    pullY,
    refreshing,
    threshold: PULL_THRESHOLD,
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
  };
}
